import { useCallback, useRef, useState } from "react";
import { syncShipmentsPage, withPageRetry } from "../services/catalogService";

// Walmart's Get Shipments endpoint doesn't document a hard max limit — 50
// matches the other page-based syncs in this app, so we're not guessing
// past what's proven safe.
const PAGE_LIMIT = 50;

// A tiny, per-component live sync — NOT a shared background session.
// Each screen that calls this gets its own independent instance: call
// sync(n, onPage, daysBack) to fetch up to n pages of live Walmart
// shipment data (importing anything new, refreshing status on anything
// already known), tracking which inbound_order_ids have been touched so
// the caller can badge everything else as "still updating."
//
// daysBack bounds the query itself (via fromCreatedDate on the backend) —
// Get Shipments has no documented sort order, so we can't safely infer
// "we've gone far enough back" from the shape of what a page returns.
// Bounding the query is the reliable alternative: once Walmart's own
// `done` comes back true for a given daysBack, that means every shipment
// within that window has been checked, full stop, regardless of order.
export default function useLiveShipmentSync() {
    const [touchedIds, setTouchedIds] = useState(() => new Set());
    const [done, setDone] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState("");
    const [checkedCount, setCheckedCount] = useState(0);
    const offsetRef = useRef(0);
    const doneRef = useRef(false);
    const runningRef = useRef(false);
    const daysBackRef = useRef(null);

    const sync = useCallback(async (maxPages, onPage, daysBack) => {
        if (runningRef.current || doneRef.current) return;

        // A different daysBack bound than whatever we've been scanning
        // under means a different underlying result set — the current
        // offset (and done/checkedCount/touchedIds) belonged to the OLD
        // bound and can't be reused, so start this bound fresh rather than
        // silently mixing two different windows together.
        if (daysBack != null && daysBackRef.current !== daysBack) {
            daysBackRef.current = daysBack;
            offsetRef.current = 0;
            doneRef.current = false;
            setDone(false);
            setCheckedCount(0);
            setTouchedIds(new Set());
        }

        runningRef.current = true;
        setSyncing(true);
        setError("");
        try {
            for (let i = 0; i < maxPages && !doneRef.current; i++) {
                // Same retry-with-backoff net used everywhere else — this
                // sync previously had NONE: a single transient failure
                // threw uncaught (no .catch() at any call site), silently
                // stopping the sync forever with `done` stuck false.
                const res = await withPageRetry(() =>
                    syncShipmentsPage(offsetRef.current, PAGE_LIMIT, daysBackRef.current ?? 365)
                );
                offsetRef.current = res.next_offset;
                doneRef.current = res.done;
                setDone(res.done);
                setCheckedCount((c) => c + PAGE_LIMIT);
                setTouchedIds((prev) => {
                    const next = new Set(prev);
                    [...(res.imported || []), ...(res.skipped || []), ...(res.status_refreshed || [])]
                        .forEach((id) => next.add(id));
                    return next;
                });
                if (onPage) await onPage();
            }
        } catch (e) {
            setError(e.message || "Failed to sync shipments from Walmart");
        } finally {
            runningRef.current = false;
            setSyncing(false);
        }
    }, []);

    return {
        touchedIds, done, syncing, error, checkedCount, sync,
        // Whether the current `done` reflects a bounded (daysBack-limited)
        // scan, vs the old unbounded 365-day default — lets callers show
        // "nothing further within your range" instead of a generic
        // "checked everything" message.
        isBounded: daysBackRef.current != null,
    };
}